<template>
  <a-modal
    :visible="visible"
    title="Review Worker Assignment"
    width="860px"
    :destroy-on-close="true"
    :footer="null"
    @cancel="close"
  >
    <div v-if="loading && !assignmentRequest" class="assignment-request-review__loading"><a-spin /></div>

    <div class="flex flex-column gap-2" v-else-if="assignmentRequest">
      <div class="assignment-request-review__header">
        <grouped-associate-info
          :first-name="assignmentRequest.worker?.first_name"
          :last-name="assignmentRequest.worker?.last_name"
          :photo-url="assignmentRequest.worker?.photo_url"
          :associate-number="assignmentRequest.worker?.associate_number"
          :role="assignmentRequest.worker?.role?.name"
          :unverified="!assignmentRequest.worker?.is_verified"
          :payment-info="assignmentRequest.worker?.payment_info"
        />
        <a-tag :color="status.color"> {{ status.title }} </a-tag>
      </div>

      <a-descriptions :column="2" size="small" :colon="false" class="assignment-request-review__details">
        <a-descriptions-item label="Staff Company">{{
          assignmentRequest.staff_company?.name || '-'
        }}</a-descriptions-item>
        <a-descriptions-item label="Facility">{{ assignmentRequest.facility?.name || '-' }}</a-descriptions-item>
        <a-descriptions-item label="Position">{{ assignmentRequest.worker?.role?.name || '-' }}</a-descriptions-item>
        <a-descriptions-item label="Rate">{{ formattedRate }}</a-descriptions-item>
        <a-descriptions-item label="Requested At">{{ formatDate(assignmentRequest.requested_at) }}</a-descriptions-item>
        <a-descriptions-item label="Reviewed At">{{ formatDate(assignmentRequest.reviewed_at) }}</a-descriptions-item>
        <a-descriptions-item label="Reviewed By">{{ reviewedBy }}</a-descriptions-item>
        <a-descriptions-item v-if="assignmentRequest.reject_reason" label="Reject Reason">
          {{ assignmentRequest.reject_reason }}
        </a-descriptions-item>
      </a-descriptions>

      <h3>Documents</h3>
      <div v-if="documentFiles.length" class="assignment-request-review__documents">
        <div v-for="file in documentFiles" :key="file.key" class="assignment-request-review__document">
          <div>
            <strong>{{ file.documentName }}</strong>
            <div class="assignment-request-review__document-meta">
              {{ file.typeName }} · {{ formatDate(file.createdAt) }}
            </div>
          </div>
          <div class="flex gap-2">
            <a-button size="small" icon="eye" @click="viewDocument(file)">View</a-button>
            <a-button size="small" icon="download" @click="downloadDocument(file)">Download</a-button>
          </div>
        </div>
      </div>
      <a-empty v-else description="No documents available" />

      <a-alert
        v-if="isPending && !canReview"
        type="info"
        show-icon
        class="mt-3"
        message="You do not have permission to review worker assignments."
      />

      <div class="assignment-request-review__footer">
        <a-button @click="close">Close</a-button>
        <div v-if="isPending && canReview" class="flex gap-2">
          <a-button type="danger" :disabled="submitting" @click="openDecision('reject')">Reject Assignment</a-button>
          <a-button type="primary" :disabled="submitting" @click="openDecision('accept')">Accept Worker</a-button>
        </div>
      </div>
    </div>

    <a-modal
      v-if="decision"
      :visible="!!decision"
      :title="decisionTitle"
      :ok-text="decision === 'accept' ? 'Accept Worker' : 'Reject Assignment'"
      cancel-text="Continue Reviewing"
      :confirm-loading="submitting"
      :ok-button-props="decision === 'reject' ? { disabled: !trimmedRejectReason } : {}"
      @cancel="closeDecision"
      @ok="submitDecision"
    >
      <p v-if="decision === 'accept'">
        {{ workerName }} will be added to the facility and become available for scheduling.
      </p>
      <a-form-model v-else :model="{ reason: rejectReason }">
        <a-form-model-item label="Reason for rejection" required>
          <a-textarea v-model="rejectReason" :auto-size="{ minRows: 3, maxRows: 6 }" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <assignment-request-pdf-viewer :visible.sync="pdfViewerVisible" :file="selectedPdfFile" />
  </a-modal>
</template>

<script>
import { Alert, Button, Descriptions, Empty, FormModel, Input, Modal, Spin, Tag, notification } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'
import util from '@/util'
import { ASSIGNMENT_REQUEST_STATUS } from '@/store/modules/profile/constants'
import AssignmentRequestPdfViewer from '../assignment-requests/assignment-request-pdf-viewer.vue'
import { canReviewAssignmentRequests, formatAssignmentRate } from '../../utils/assignment-requests'

const { mapActions: profileActions } = createNamespacedHelpers('profile')
const { mapActions: alertActions } = createNamespacedHelpers('alerts')

export default {
  components: {
    'a-alert': Alert,
    'a-button': Button,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-empty': Empty,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-modal': Modal,
    'a-spin': Spin,
    'a-tag': Tag,
    'a-textarea': Input.TextArea,
    'assignment-request-pdf-viewer': AssignmentRequestPdfViewer,
    'grouped-associate-info': GroupedAssociateInfo
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    requestId: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      assignmentRequest: undefined,
      decision: undefined,
      rejectReason: '',
      pdfViewerVisible: false,
      selectedPdfFile: undefined
    }
  },
  computed: {
    currentAssociate() {
      return this.$store.state.applicationState.associate
    },
    currentProject() {
      return this.$store.state.applicationState.currentProject
    },
    canReview() {
      return canReviewAssignmentRequests(this.currentAssociate, this.currentProject)
    },
    isPending() {
      return this.assignmentRequest?.status === ASSIGNMENT_REQUEST_STATUS.pending.key
    },
    workerName() {
      const worker = this.assignmentRequest?.worker
      return [worker?.first_name, worker?.last_name].filter(Boolean).join(' ') || 'this worker'
    },
    reviewedBy() {
      const reviewer = this.assignmentRequest?.reviewed_by
      return reviewer ? [reviewer.first_name, reviewer.last_name].filter(Boolean).join(' ') || '-' : '-'
    },
    formattedRate() {
      return formatAssignmentRate(this.assignmentRequest?.hourly_rate)
    },
    status() {
      return ASSIGNMENT_REQUEST_STATUS[this.assignmentRequest?.status] || { title: '-', color: 'default' }
    },
    decisionTitle() {
      return this.decision === 'accept'
        ? `Accept ${this.workerName} for this facility?`
        : `Reject Assignment for ${this.workerName}?`
    },
    trimmedRejectReason() {
      return this.rejectReason.trim()
    },
    documentFiles() {
      return (this.assignmentRequest?.documents ?? []).flatMap(document =>
        (document.files ?? [])
          .filter(file => file?.url)
          .map((file, index) => ({
            ...file,
            key: `${document.id}:${file.id ?? index}`,
            documentName: document.name || 'Document',
            typeName: document.type?.name || 'Document',
            createdAt: document.created_at
          }))
      )
    }
  },
  watch: {
    visible: {
      handler(isVisible) {
        if (isVisible && this.requestId) {
          this.fetchAssignmentRequest()
        }
      },
      immediate: true
    },
    requestId(requestId) {
      if (this.visible && requestId) {
        this.fetchAssignmentRequest()
      }
    }
  },
  methods: {
    ...profileActions(['acceptAssignmentRequest', 'getAssignmentRequest', 'rejectAssignmentRequest']),
    ...alertActions(['getAlertsCount', 'removeWorkerAssignmentReviewAlert']),
    formatAssignmentRate,

    showNotification(type, message, description) {
      notification[type]({ message, description })
    },
    formatDate(value) {
      return util.convertDateTimeToCurrentTimeZoneWithFormat(value, 'MM/DD/YYYY h:mm A') || '-'
    },
    async fetchAssignmentRequest(showError = true) {
      this.loading = true

      try {
        this.assignmentRequest = await this.getAssignmentRequest({ request_id: this.requestId })
      } catch (error) {
        if (showError) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        }
      } finally {
        this.loading = false
      }
    },
    close() {
      this.decision = undefined
      this.rejectReason = ''
      this.assignmentRequest = undefined
      this.$emit('update:visible', false)
    },
    openDecision(decision) {
      this.decision = decision
      this.rejectReason = ''
    },
    closeDecision() {
      if (!this.submitting) {
        this.decision = undefined
        this.rejectReason = ''
      }
    },
    isPdf(file) {
      return /\.pdf(?:$|[?#])/i.test(file?.url || '')
    },
    viewDocument(file) {
      if (this.isPdf(file)) {
        this.selectedPdfFile = file
        this.pdfViewerVisible = true
        return
      }

      window.open(file.url, '_blank', 'noopener')
    },
    downloadDocument(file) {
      util.downloadFile(file.url, file.documentName)
    },
    async submitDecision() {
      if (!this.assignmentRequest || this.submitting || (this.decision === 'reject' && !this.trimmedRejectReason)) {
        return
      }

      this.submitting = true

      try {
        const payload = { request_id: this.assignmentRequest.id }
        const updatedRequest =
          this.decision === 'accept'
            ? await this.acceptAssignmentRequest(payload)
            : await this.rejectAssignmentRequest({ ...payload, reason: this.trimmedRejectReason })

        this.assignmentRequest = { ...this.assignmentRequest, ...updatedRequest }
        this.decision = undefined
        this.rejectReason = ''
        await this.removeWorkerAssignmentReviewAlert({ requestId: this.assignmentRequest.id })
        await this.getAlertsCount()
        this.$emit('reviewed', this.assignmentRequest)
        await this.fetchAssignmentRequest(false)
        this.showNotification('success', 'Success', `Assignment request ${this.assignmentRequest.status}.`)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        await this.fetchAssignmentRequest(false)
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.assignment-request-review__loading {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.assignment-request-review__header,
.assignment-request-review__document,
.assignment-request-review__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.assignment-request-review__details {
  margin-top: 20px;
}

.assignment-request-review__documents {
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow: auto;
  gap: 8px;
}

.assignment-request-review__document {
  padding: 12px;
  border: 1px solid var(--border-color-base);
  border-radius: 8px;
  background: var(--bg-component);
}

.assignment-request-review__document-meta {
  margin-top: 4px;
  color: var(--text-color-secondary);
  font-size: 12px;
}

.assignment-request-review__footer {
  margin-top: 24px;
}
</style>
